import { Button } from 'component/Button'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { DashedButton } from 'page/ProfileScreen/basic/DashedButton'
import { SectionTitle } from 'page/ProfileScreen/basic/SectionTitle'
import { ErrorPopover } from 'page/ProfileScreen/ErrorPopover/ErrorPopover'
import { Genders } from 'page/ProfileScreen/Genders/Genders'
import { SelfieHelpStep } from 'page/ProfileScreen/HelpStep/SelfieHelpStep'
import { CameraInput } from 'page/ProfileScreen/Selfies/CameraInput'
import { ChangeEvent, useRef } from 'react'
import { store } from 'store/store'

export const SelfieStep = observer(() => {
  const { selfies, camera } = store.profile.common
  const selfie = selfies[0]
  const done = !!selfie.url
  const file = useRef<HTMLInputElement>(null)

  const onHow = useLatestCallback(() => {
    store.profile.step = 'help'
  })

  const onFile = useLatestCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) await selfie.setFile(file)
  })

  const onImage = useLatestCallback(() => {
    file.current?.click()
  })

  const onTake = useLatestCallback(async () => {
    await store.profile.common.take()
  })

  const onFlip = useLatestCallback(async () => {
    await camera.switchCamera()
  })

  const onRepeat = useLatestCallback(() => {
    selfie.setUrl(undefined)
  })

  const onContinue = useLatestCallback(async () => {
    store.profile.simple.stage = 'full'
  })

  return <Vertical wide tall gap={32}>
    <Vertical gap={12}>
      <SectionTitle>
        {i18n('profile.Gender')}
      </SectionTitle>
      <Genders />
    </Vertical>

    <Vertical tall wide gap={16} stable center>
      <Space wide stable center>
        <CameraInput simple />
        <ErrorPopover selfie={selfie} />
      </Space>

      {!done && <Vertical gap={12} center>
        <Typo size={12} center upper pre>
          {i18n('TakeSelfieYouNeedGoodLighting')}
        </Typo>
        <DashedButton onClick={onHow}>
          {i18n('HowToMake')}
        </DashedButton>
      </Vertical>}

      {done && <Space center>
        <Typo size={12} center upper pre>
          {i18n('Done')}
        </Typo>
      </Space>}

      <input ref={file} style={{ display: 'none' }} type="file" accept="image/*" onChange={onFile} />

      {store.profile.step === 'help' && <SelfieHelpStep />}
    </Vertical>

    {done && <Space gap={16} wide stable columns>
      <Button gap={8} pad={0} size={48} flat action={onRepeat}>
        <Icon size={16} name="autorenew" />
        <Typo size={13} medium upper>
          {i18n('Repeat')}
        </Typo>
      </Button>
      <Button gap={8} pad={0} size={48} primary action={onContinue}>
        <Typo size={13} medium upper>
          {i18n('Continue')}
        </Typo>
        <Icon size={16} name="chevron_right" />
      </Button>
    </Space>}

    {!done && <Space gap={16} pad={[0, 32]} opposite stable>
      <Button size={48} flat round action={onImage}>
        <Icon size={16} name="image" />
      </Button>
      <Button size={64} primary round action={onTake}>
        <Icon size={16} name="photo_camera" fill />
      </Button>
      <Button size={48} flat round action={onFlip}>
        <Icon size={16} name="autorenew" />
      </Button>
    </Space>}
  </Vertical>
})
